import React, { useState } from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from './SurveyTemplates.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_SURVEY_TEMPLATE_IDS,
  GET_SURVEY_TEMPLATES,
  SurveyTemplateItem,
} from './SurveyTemplates.query';
import { RouteComponentProps, withRouter } from 'react-router';
import Layout from '../../../../components/Layout/Layout';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import { useHistory } from 'react-router-dom';
import useMode from '../../../../hooks/useMode';
import Button from '../../../../components/Button/Button';
import { getStringSearchOr } from '../../../../helpers/search';
import ResetFiltersButton from '../../../../components/FilterBar/ResetFiltersButton';
import { SearchInput } from '../../../../components/Form';
import { Flex } from 'rebass';

type SurveyTemplateDetailParams = {
  id?: string;
};

type SurveyTemplateDetailProps =
  RouteComponentProps<SurveyTemplateDetailParams>;

export const searchFields = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'description',
    label: 'Description',
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: any) => {
  return {
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component for admin survey templates page
 */
function SurveyTemplates({ match }: SurveyTemplateDetailProps): JSX.Element {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<any>(convertFilters, 'adminSurveyTemplates', {
      stringSearch: undefined,
    });

  const { orderBy, onHeaderClick } = useOrderBy(
    'adminSurveyTemplates',
    availableColumns,
    'id'
  );

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<SurveyTemplateItem>(
    'waterHazardSurveyTemplates',
    GET_SURVEY_TEMPLATE_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_SURVEY_TEMPLATES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(
      availableColumns,
      'storedColumnConfigurationAdminSurveyTemplates'
    );

  const history = useHistory();
  const mode = useMode();

  const { onRowClick } = useThirdPaneCallbacks(
    `/${mode}/survey-templates`,
    itemIds
  );

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Survey Templates">
          <Button
            ml={3}
            variant="primary"
            icon="plus"
            onClick={() => history.push(`/${mode}/survey-templates/add`)}
          >
            Add Template
          </Button>
        </PageBar>
        <Flex variant="filterBar.container">
          <Flex variant="filterBar.left">
            <SearchInput
              placeholder="Search templates"
              width={300}
              name="stringSearch"
              value={
                storedFilters.stringSearch === undefined
                  ? ''
                  : storedFilters.stringSearch
              }
              onChange={(e) =>
                setStoredFilters({
                  ...storedFilters,
                  stringSearch: e.target.value,
                })
              }
            />
          </Flex>
          <Flex variant="filterBar.right">
            <ResetFiltersButton setFilters={setStoredFilters} />
          </Flex>
        </Flex>
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No survey templates found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </Flex>
    </Layout>
  );
}

export default withRouter(SurveyTemplates);
